import React, { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from 'react-query'
import { Link } from 'react-router-dom'
import {
  Search,
  Filter,
  Download,
  Plus,
  MoreHorizontal,
  Edit,
  Trash2,
  DollarSign,
  Eye,
  User as UserIcon,
  CreditCard
} from 'lucide-react'
import toast from 'react-hot-toast'
import { unifiedAdminApiClient as adminApi } from '../services/unified-admin-api-client'
import { User, UserFilters, CreditAdjustmentForm } from '../types'
import { useAuthStore } from '../stores/authStore'

export default function UsersPage() {
  const [page, setPage] = useState(1)
  const [filters, setFilters] = useState<UserFilters>({})
  const [showFilters, setShowFilters] = useState(false)
  const [selectedUsers, setSelectedUsers] = useState<string[]>([])
  const [showCreditModal, setShowCreditModal] = useState(false)
  const [selectedUser, setSelectedUser] = useState<User | null>(null)
  
  const { checkPermission } = useAuthStore()
  const queryClient = useQueryClient()

  const { data: usersData, isLoading, error } = useQuery(
    ['users', page, filters],
    () => adminApi.getUsers(page, 20, filters),
    {
      keepPreviousData: true,
    }
  )

  const deleteUserMutation = useMutation(
    (userId: string) => adminApi.deleteUser(userId),
    {
      onSuccess: () => {
        toast.success('User deleted successfully')
        queryClient.invalidateQueries('users')
      },
      onError: () => {
        toast.error('Failed to delete user')
      },
    }
  )

  const adjustCreditsMutation = useMutation(
    ({ userId, data }: { userId: string; data: CreditAdjustmentForm }) =>
      adminApi.adjustUserCredits(userId, data),
    {
      onSuccess: () => {
        toast.success('Credits adjusted successfully')
        queryClient.invalidateQueries('users')
        setShowCreditModal(false)
        setSelectedUser(null)
      },
      onError: () => {
        toast.error('Failed to adjust credits')
      },
    }
  )

  const handleSearch = (searchTerm: string) => {
    setFilters({ ...filters, search: searchTerm })
    setPage(1)
  }

  const handleFilterChange = (newFilters: Partial<UserFilters>) => {
    setFilters({ ...filters, ...newFilters })
    setPage(1)
  }

  const handleSelectUser = (userId: string) => {
    setSelectedUsers(prev =>
      prev.includes(userId)
        ? prev.filter(id => id !== userId)
        : [...prev, userId]
    )
  }

  const handleSelectAll = () => {
    if (selectedUsers.length === usersData?.data.length) {
      setSelectedUsers([])
    } else {
      setSelectedUsers(usersData?.data.map(user => user.id) || [])
    }
  }

  const handleDeleteUser = (user: User) => {
    if (window.confirm(`Are you sure you want to delete user ${user.email}?`)) {
      deleteUserMutation.mutate(user.id)
    }
  }

  const handleAdjustCredits = (user: User) => {
    setSelectedUser(user)
    setShowCreditModal(true)
  }

  const handleBulkExport = async () => {
    try {
      const blob = await adminApi.exportUserData(selectedUsers.join(','))
      const url = window.URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = `users-export-${new Date().toISOString().split('T')[0]}.csv`
      document.body.appendChild(a)
      a.click()
      window.URL.revokeObjectURL(url)
      document.body.removeChild(a)
      toast.success('Users exported successfully')
    } catch (error) {
      toast.error('Failed to export users')
    }
  }

  if (isLoading) {
    return <UsersPageSkeleton />
  }

  if (error) {
    return (
      <div className="text-center py-12">
        <p className="text-red-600">Failed to load users. Please try again.</p>
      </div>
    )
  }

  const users = usersData?.data || []
  const totalPages = usersData?.total_pages || 1

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="md:flex md:items-center md:justify-between">
        <div className="flex-1 min-w-0">
          <h2 className="text-2xl font-bold leading-7 text-gray-900 sm:text-3xl sm:truncate">
            Users
          </h2>
          <p className="mt-1 text-sm text-gray-500">
            Manage user accounts, credits, and permissions
          </p>
        </div>
        <div className="mt-4 flex space-x-3 md:mt-0 md:ml-4">
          {selectedUsers.length > 0 && (
            <button
              onClick={handleBulkExport}
              className="btn-secondary"
            >
              <Download className="h-4 w-4 mr-2" />
              Export ({selectedUsers.length})
            </button>
          )}
          {checkPermission('users:write') && (
            <button className="btn-primary">
              <Plus className="h-4 w-4 mr-2" />
              Add User
            </button>
          )}
        </div>
      </div>

      {/* Search and Filters */}
      <div className="card">
        <div className="card-body">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="flex-1">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search users by email, name, or company..."
                  className="input pl-10"
                  value={filters.search || ''}
                  onChange={(e) => handleSearch(e.target.value)}
                />
              </div>
            </div>
            <button
              onClick={() => setShowFilters(!showFilters)}
              className="btn-secondary"
            >
              <Filter className="h-4 w-4 mr-2" />
              Filters
            </button>
          </div>

          {showFilters && (
            <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              <select
                className="input"
                value={filters.user_type || ''}
                onChange={(e) => handleFilterChange({ user_type: e.target.value || undefined })}
              >
                <option value="">All User Types</option>
                <option value="agent">Agent</option>
                <option value="broker">Broker</option>
                <option value="admin">Admin</option>
              </select>

              <select
                className="input"
                value={filters.is_active === undefined ? '' : filters.is_active.toString()}
                onChange={(e) => handleFilterChange({ 
                  is_active: e.target.value === '' ? undefined : e.target.value === 'true' 
                })}
              >
                <option value="">All Status</option>
                <option value="true">Active</option>
                <option value="false">Inactive</option>
              </select>

              <input
                type="date"
                className="input"
                placeholder="Created After"
                value={filters.created_after || ''}
                onChange={(e) => handleFilterChange({ created_after: e.target.value || undefined })}
              />

              <input
                type="number"
                className="input"
                placeholder="Min Credits"
                value={filters.min_credits || ''}
                onChange={(e) => handleFilterChange({ 
                  min_credits: e.target.value ? parseInt(e.target.value) : undefined 
                })}
              />
            </div>
          )}
        </div>
      </div>

      {/* Users Table */}
      <div className="card">
        <div className="card-body p-0">
          <div className="overflow-x-auto">
            <table className="table">
              <thead>
                <tr>
                  <th className="w-12">
                    <input
                      type="checkbox"
                      checked={selectedUsers.length === users.length && users.length > 0}
                      onChange={handleSelectAll}
                      className="rounded border-gray-300"
                    />
                  </th>
                  <th>User</th>
                  <th>Type</th>
                  <th>Status</th>
                  <th>Credits</th>
                  <th>Joined</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {users.map((user) => (
                  <UserRow
                    key={user.id}
                    user={user}
                    selected={selectedUsers.includes(user.id)}
                    onSelect={() => handleSelectUser(user.id)}
                    onDelete={() => handleDeleteUser(user)}
                    onAdjustCredits={() => handleAdjustCredits(user)}
                    canEdit={checkPermission('users:write')}
                    canDelete={checkPermission('users:delete')}
                  />
                ))}
              </tbody>
            </table>
          </div>

          {users.length === 0 && (
            <div className="text-center py-12">
              <UserIcon className="mx-auto h-12 w-12 text-gray-400" />
              <h3 className="mt-2 text-sm font-medium text-gray-900">No users found</h3>
              <p className="mt-1 text-sm text-gray-500">
                {filters.search ? 'Try adjusting your search criteria.' : 'Get started by adding a new user.'}
              </p>
            </div>
          )}
        </div>
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <Pagination
          currentPage={page}
          totalPages={totalPages}
          onPageChange={setPage}
        />
      )}

      {/* Credit Adjustment Modal */}
      {showCreditModal && selectedUser && (
        <CreditAdjustmentModal
          user={selectedUser}
          onClose={() => {
            setShowCreditModal(false)
            setSelectedUser(null)
          }}
          onSubmit={(data) => adjustCreditsMutation.mutate({ userId: selectedUser.id, data })}
          isLoading={adjustCreditsMutation.isLoading}
        />
      )}
    </div>
  )
}

interface UserRowProps {
  user: User
  selected: boolean
  onSelect: () => void
  onDelete: () => void
  onAdjustCredits: () => void
  canEdit: boolean
  canDelete: boolean
}

function UserRow({ user, selected, onSelect, onDelete, onAdjustCredits, canEdit, canDelete }: UserRowProps) {
  const [showMenu, setShowMenu] = useState(false)

  return (
    <tr>
      <td>
        <input
          type="checkbox"
          checked={selected}
          onChange={onSelect}
          className="rounded border-gray-300"
        />
      </td>
      <td>
        <div className="flex items-center">
          <div className="flex-shrink-0 h-10 w-10">
            <div className="h-10 w-10 rounded-full bg-gray-300 flex items-center justify-center">
              <span className="text-sm font-medium text-gray-700">
                {user.first_name?.charAt(0) || user.email.charAt(0).toUpperCase()}
              </span>
            </div>
          </div>
          <div className="ml-4">
            <div className="text-sm font-medium text-gray-900">
              {user.first_name && user.last_name 
                ? `${user.first_name} ${user.last_name}`
                : user.first_name || user.last_name || user.email.split('@')[0] || 'No name'
              }
            </div>
            <div className="text-sm text-gray-500">{user.email}</div>
            {user.company && (
              <div className="text-xs text-gray-400">{user.company}</div>
            )}
          </div>
        </div>
      </td>
      <td>
        <span className="badge badge-gray capitalize">
          {user.user_type}
        </span>
      </td>
      <td>
        <span className={`badge ${user.is_active ? 'badge-success' : 'badge-danger'}`}>
          {user.is_active ? 'Active' : 'Inactive'}
        </span>
        {user.is_verified && (
          <span className="inline ml-1 text-green-500 text-xs">✓</span>
        )}
      </td>
      <td>
        <div className="flex items-center">
          <CreditCard className="h-4 w-4 text-gray-400 mr-1" />
          <span className="text-sm font-medium">{user.credits}</span>
        </div>
      </td>
      <td>
        <div className="text-sm text-gray-900">
          {new Date(user.created_at).toLocaleDateString()}
        </div>
      </td>
      <td>
        <div className="flex items-center space-x-2">
          <Link
            to={`/users/${user.id}`}
            className="text-primary-600 hover:text-primary-900"
          >
            <Eye className="h-4 w-4" />
          </Link>
          
          {canEdit && (
            <div className="relative">
              <button
                onClick={() => setShowMenu(!showMenu)}
                className="text-gray-400 hover:text-gray-600"
              >
                <MoreHorizontal className="h-4 w-4" />
              </button>
              
              {showMenu && (
                <div className="absolute right-0 mt-2 w-48 bg-white rounded-md shadow-lg z-10">
                  <div className="py-1">
                    <button
                      onClick={() => {
                        onAdjustCredits()
                        setShowMenu(false)
                      }}
                      className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 w-full text-left"
                    >
                      <DollarSign className="inline h-4 w-4 mr-2" />
                      Adjust Credits
                    </button>
                    <Link
                      to={`/users/${user.id}`}
                      className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                      onClick={() => setShowMenu(false)}
                    >
                      <Edit className="inline h-4 w-4 mr-2" />
                      Edit User
                    </Link>
                    {canDelete && (
                      <button
                        onClick={() => {
                          onDelete()
                          setShowMenu(false)
                        }}
                        className="block px-4 py-2 text-sm text-red-700 hover:bg-red-50 w-full text-left"
                      >
                        <Trash2 className="inline h-4 w-4 mr-2" />
                        Delete User
                      </button>
                    )}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </td>
    </tr>
  )
}

interface PaginationProps {
  currentPage: number
  totalPages: number
  onPageChange: (page: number) => void
}

function Pagination({ currentPage, totalPages, onPageChange }: PaginationProps) {
  const pages = []
  const maxVisible = 5

  let startPage = Math.max(1, currentPage - Math.floor(maxVisible / 2))
  let endPage = Math.min(totalPages, startPage + maxVisible - 1)

  if (endPage - startPage + 1 < maxVisible) {
    startPage = Math.max(1, endPage - maxVisible + 1)
  }

  for (let i = startPage; i <= endPage; i++) {
    pages.push(i)
  }

  return (
    <div className="flex items-center justify-between">
      <div className="flex-1 flex justify-between sm:hidden">
        <button
          onClick={() => onPageChange(currentPage - 1)}
          disabled={currentPage === 1}
          className="btn-secondary"
        >
          Previous
        </button>
        <button
          onClick={() => onPageChange(currentPage + 1)}
          disabled={currentPage === totalPages}
          className="btn-secondary"
        >
          Next
        </button>
      </div>
      <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
        <div>
          <p className="text-sm text-gray-700">
            Page <span className="font-medium">{currentPage}</span> of{' '}
            <span className="font-medium">{totalPages}</span>
          </p>
        </div>
        <div>
          <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px">
            <button
              onClick={() => onPageChange(currentPage - 1)}
              disabled={currentPage === 1}
              className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50"
            >
              Previous
            </button>
            {pages.map((page) => (
              <button
                key={page}
                onClick={() => onPageChange(page)}
                className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium ${
                  page === currentPage
                    ? 'z-10 bg-primary-50 border-primary-500 text-primary-600'
                    : 'bg-white border-gray-300 text-gray-500 hover:bg-gray-50'
                }`}
              >
                {page}
              </button>
            ))}
            <button
              onClick={() => onPageChange(currentPage + 1)}
              disabled={currentPage === totalPages}
              className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50"
            >
              Next
            </button>
          </nav>
        </div>
      </div>
    </div>
  )
}

interface CreditAdjustmentModalProps {
  user: User
  onClose: () => void
  onSubmit: (data: CreditAdjustmentForm) => void
  isLoading: boolean
}

function CreditAdjustmentModal({ user, onClose, onSubmit, isLoading }: CreditAdjustmentModalProps) {
  const [amount, setAmount] = useState('')
  const [reason, setReason] = useState('')

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    onSubmit({
      amount: parseInt(amount),
      reason,
    })
  }

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
      <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
        <div className="mt-3">
          <h3 className="text-lg font-medium text-gray-900 mb-4">
            Adjust Credits for {user.email}
          </h3>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="label">Amount</label>
              <input
                type="number"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                className="input"
                placeholder="Enter amount (positive to add, negative to subtract)"
                required
              />
            </div>
            <div>
              <label className="label">Reason</label>
              <textarea
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                className="input"
                rows={3}
                placeholder="Reason for credit adjustment"
                required
              />
            </div>
            <div className="flex justify-end space-x-3">
              <button
                type="button"
                onClick={onClose}
                className="btn-secondary"
                disabled={isLoading}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="btn-primary"
                disabled={isLoading || !amount || !reason}
              >
                {isLoading ? 'Adjusting...' : 'Adjust Credits'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  )
}

function UsersPageSkeleton() {
  return (
    <div className="space-y-6 animate-pulse">
      <div className="h-8 bg-gray-200 rounded w-1/4"></div>
      <div className="card">
        <div className="card-body">
          <div className="h-10 bg-gray-200 rounded"></div>
        </div>
      </div>
      <div className="card">
        <div className="card-body p-0">
          <div className="space-y-4 p-6">
            {[...Array(5)].map((_, i) => (
              <div key={i} className="flex items-center space-x-4">
                <div className="h-10 w-10 bg-gray-200 rounded-full"></div>
                <div className="flex-1 space-y-2">
                  <div className="h-4 bg-gray-200 rounded w-1/4"></div>
                  <div className="h-3 bg-gray-200 rounded w-1/6"></div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  )
}